import { onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';

/**
 * The "reload when the filter context changes" trio that was copy-pasted across
 * charts, grids and card arrays: a deep watch on `filtersLastUpdated`, a
 * `dataLoaded` false→true watch, and an `onMounted` initial load.
 *
 * Register this AFTER the component's own `onMounted`, so the component's setup
 * work (levels, breadcrumbs, …) happens before the first `cb()`.
 *
 * @param {object|import('vue').Ref} filterContext  duck type: { filtersLastUpdated, … }
 * @param {boolean|import('vue').Ref} dataLoaded
 * @param {() => any} cb                            the reload
 * @param {{ debounce?: number, immediate?: boolean, requireDataLoaded?: boolean }} [options]
 */
export function useFilterContextRefresh(filterContext, dataLoaded, cb, options = {}) {
  const { debounce = 0, immediate = true, requireDataLoaded = true } = options;
  const isMounted = ref(false);
  let timer = null;

  const refresh = () => {
    if (!isMounted.value) return;
    if (requireDataLoaded && !unref(dataLoaded)) return;
    if (!debounce) { cb(); return; }
    clearTimeout(timer);
    timer = setTimeout(cb, debounce);
  };

  // deep:true is REQUIRED — filtersLastUpdated is a ref held as an own property
  // of a markRaw context (createFilterContext), so it is never auto-unwrapped.
  // A watch getter returning a ref is not unwrapped; only traverse() under
  // `deep` reads .value and registers the dependency. Drop `deep` and every
  // reload in the app silently stops.
  watch(() => unref(filterContext)?.filtersLastUpdated, refresh, { deep: true });
  watch(() => unref(dataLoaded), (v) => { if (v) refresh(); });

  onMounted(() => { isMounted.value = true; if (immediate) refresh(); });
  onBeforeUnmount(() => clearTimeout(timer));

  return { refresh, isMounted };
}
